/**
 * usePassing - a passing moment on a plate (Squad Mode stage 8): true for `ms` after `key` changes in a way `fires`
 * calls a moment. The first key a plate sees never is one (a reload, a rejoin, a panel opened: that already happened),
 * so a moment is never replayed. The moment always runs its whole length: a later change of key neither cuts it short
 * nor leaves it stuck on (the stage 8 review); a new moment while one plays starts the clock again.
 *
 * @param key - what the moment is read from, as a string
 * @param fires - (before, now) => whether this change is a moment; module-level, so its identity holds
 * @param ms - how long it plays
 */
import { useEffect, useRef, useState } from 'react';

export default function usePassing(key: string, fires: (before: string, now: string) => boolean, ms: number): boolean {
  const was = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const before = was.current;
    was.current = key;
    if (before === null || !fires(before, key)) return;
    setPlaying(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setPlaying(false), ms);
  }, [key, fires, ms]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return playing;
}
